<template>
  <div class="settings-section" data-test-id="program-type-setting">
    <p class="settings-section__title">{{ t('admin.programSettings.programType.title') }}</p>
    <p class="text-sm text-muted-foreground">{{ t('admin.programSettings.programType.hint') }}</p>
    <div class="flex flex-wrap items-end gap-3">
      <div class="flex flex-col gap-1.5">
        <label id="program-type-label" class="input-label">{{ t('admin.programSettings.programType.label') }}</label>
        <Select v-model="typeRef">
          <SelectTrigger aria-labelledby="program-type-label" data-test-id="program-type-select" class="w-72">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              v-for="option in OPTIONS"
              :key="option"
              :value="option"
              :data-test-id="`program-type-option-${option}`"
            >
              {{ t(`admin.programSettings.programType.option.${option}`) }}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button
        type="button"
        variant="outline"
        data-test-id="program-type-save"
        :disabled="!isDirtyRef || isSavingRef"
        @click="saveType()"
      >
        {{ t('admin.programSettings.programType.save') }}
      </Button>
    </div>
    <p class="text-sm text-muted-foreground" data-test-id="program-type-description">
      {{ t(`admin.programSettings.programType.description.${typeRef}`) }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { useMutation } from '@tanstack/vue-query'
import { computed, ref, watch } from 'vue'

import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { updateProgramTypeMutation } from '@/features/programs/api'
import { PROGRAM_TYPES, type ProgramType } from '@/features/programs/types'
import useToast from '@/hooks/use-toasts.hook'
import { useI18n } from '@/lib/i18n'

// Le Select ne prend pas de valeur vide : « non déclaré » a sa propre valeur, envoyée à l'API comme null.
const UNDECLARED = 'UNDECLARED'
type OptionType = ProgramType | typeof UNDECLARED
const OPTIONS: OptionType[] = [UNDECLARED, ...PROGRAM_TYPES]

const props = defineProps<{ programId: string; value: ProgramType | undefined }>()
const emit = defineEmits<{ saved: [type: ProgramType | undefined] }>()

const { t } = useI18n()
const toast = useToast()

const typeRef = ref<OptionType>(props.value ?? UNDECLARED)
watch(
  () => props.value,
  (value) => {
    typeRef.value = value ?? UNDECLARED
  },
)
const isDirtyRef = computed(() => typeRef.value !== (props.value ?? UNDECLARED))

const { mutate: saveTypeMutation, isPending: isSavingRef } = useMutation({
  mutationFn: (type: ProgramType | null) => updateProgramTypeMutation({ programId: props.programId, type }),
  onSuccess: ({ programType }) => {
    // Pas de rechargement des programmes : il effacerait le reste du formulaire non enregistré
    emit('saved', programType ?? undefined)
    toast?.success(t('admin.programSettings.programType.saved'))
  },
  onError: () => {
    toast?.error(t('error.common'))
  },
})

function saveType() {
  saveTypeMutation(typeRef.value === UNDECLARED ? null : typeRef.value)
}
</script>
